import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type KontoBruker } from '../../lib/api.ts';

type Tab = 'foresporsler' | 'tilknytninger' | 'inviter' | 'brukere';

export function AdminKontoer() {
  const [tab, setTab] = useState<Tab>('foresporsler');
  const foresporsler = useQuery({
    queryKey: ['admin-foresporsler'],
    queryFn: () => api.adminKontoForesporsler(),
  });
  const tilknytninger = useQuery({
    queryKey: ['admin-tilknytninger'],
    queryFn: () => api.adminTilknytninger(),
  });

  const nPending = foresporsler.data?.length ?? 0;
  const nClaims = tilknytninger.data?.length ?? 0;

  const tabClass = (t: Tab) =>
    `px-3 py-2 text-sm font-medium rounded-lg ${
      tab === t ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
    }`;

  return (
    <div>
      <h1 className="text-lg font-semibold mb-4">Seilere</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <button className={tabClass('foresporsler')} onClick={() => setTab('foresporsler')}>
          Forespørsler{nPending > 0 && <span className="ml-1.5 text-amber-300">({nPending})</span>}
        </button>
        <button className={tabClass('tilknytninger')} onClick={() => setTab('tilknytninger')}>
          Tilknytninger{nClaims > 0 && <span className="ml-1.5 text-amber-300">({nClaims})</span>}
        </button>
        <button className={tabClass('inviter')} onClick={() => setTab('inviter')}>
          Invitér
        </button>
        <button className={tabClass('brukere')} onClick={() => setTab('brukere')}>
          Kontoer
        </button>
      </div>

      {tab === 'foresporsler' && <Foresporsler />}
      {tab === 'tilknytninger' && <Tilknytninger />}
      {tab === 'inviter' && <Inviter />}
      {tab === 'brukere' && <Brukere />}
    </div>
  );
}

function Foresporsler() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-foresporsler'], queryFn: () => api.adminKontoForesporsler() });

  const godkjenn = useMutation({
    mutationFn: (id: string) => api.adminGodkjennKonto(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-foresporsler'] });
      qc.invalidateQueries({ queryKey: ['admin-brukere'] });
    },
  });
  const avslaa = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.adminAvslaaKonto(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-foresporsler'] }),
  });

  if (q.isLoading) return <p className="text-slate-500 text-sm">Laster…</p>;
  const rows = q.data ?? [];
  if (rows.length === 0)
    return <p className="text-slate-500 text-sm">Ingen ventende forespørsler.</p>;

  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.id} className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="font-semibold">{r.fullName}</div>
          <div className="text-xs text-slate-400">{r.email} • {r.foreningName ?? '—'}</div>
          {(r.requestedSailNumber || r.requestedBoatName) && (
            <div className="text-xs text-slate-400 mt-1">
              Båt: <span className="font-mono">{r.requestedSailNumber}</span>
              {r.requestedBoatName && ` – ${r.requestedBoatName}`}
            </div>
          )}
          {r.requestMessage && (
            <div className="text-xs text-slate-300 mt-1 italic">«{r.requestMessage}»</div>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => godkjenn.mutate(r.id)}
              disabled={godkjenn.isPending}
              className="bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-60"
            >
              Godkjenn
            </button>
            <button
              onClick={() => {
                const reason = prompt('Grunn for avslag (valgfritt):') ?? '';
                avslaa.mutate({ id: r.id, reason });
              }}
              className="bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded text-sm"
            >
              Avslå
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Tilknytninger() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-tilknytninger'], queryFn: () => api.adminTilknytninger() });

  const godkjenn = useMutation({
    mutationFn: (id: string) => api.adminGodkjennTilknytning(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tilknytninger'] }),
  });
  const avslaa = useMutation({
    mutationFn: (id: string) => api.adminAvslaaTilknytning(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tilknytninger'] }),
  });

  if (q.isLoading) return <p className="text-slate-500 text-sm">Laster…</p>;
  const rows = q.data ?? [];
  if (rows.length === 0)
    return <p className="text-slate-500 text-sm">Ingen ventende tilknytninger.</p>;

  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.id} className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="text-sm">
            <span className="font-semibold">{r.userName}</span>{' '}
            <span className="text-slate-400">({r.userEmail})</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            vil knyttes til <span className="font-mono">{r.sailNumber}</span> {r.boatName}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => godkjenn.mutate(r.id)}
              disabled={godkjenn.isPending}
              className="bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-60"
            >
              Godkjenn
            </button>
            <button
              onClick={() => avslaa.mutate(r.id)}
              className="bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded text-sm"
            >
              Avslå
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Inviter() {
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });
  const [email, setEmail] = useState('');
  const [foreningId, setForeningId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.adminInviter(email, foreningId),
    onSuccess: () => {
      setMsg(`Invitasjon sendt til ${email}.`);
      setErr(null);
      setEmail('');
    },
    onError: (e: ApiError) => {
      setErr(e.message);
      setMsg(null);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3 max-w-md"
    >
      <p className="text-sm text-slate-400">
        Send en e-post med lenke til forespørselsskjemaet. Mottakeren fyller selv ut, og du godkjenner
        forespørselen etterpå.
      </p>
      {msg && <div className="text-emerald-300 bg-emerald-900/30 border border-emerald-800 rounded p-2 text-sm">{msg}</div>}
      {err && <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>}
      <div>
        <label className="block text-xs text-slate-400 mb-1">E-post</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Klubb</label>
        <select
          value={foreningId}
          onChange={(e) => setForeningId(e.target.value)}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Velg klubb —</option>
          {(foreninger.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        disabled={send.isPending}
        className="bg-sky-600 hover:bg-sky-500 px-4 py-2.5 rounded-lg font-semibold disabled:opacity-60"
      >
        {send.isPending ? 'Sender…' : 'Send invitasjon'}
      </button>
    </form>
  );
}

function Brukere() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const q = useQuery({
    queryKey: ['admin-brukere', status],
    queryFn: () => api.adminBrukere(status || undefined),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => api.adminSuspenderBruker(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-brukere'] }),
  });
  const activate = useMutation({
    mutationFn: (id: string) => api.adminAktiverBruker(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-brukere'] }),
  });
  // Arrangør (komité) for hjemklubben: kan styre startklokka ved komitéstyrt start.
  const arrangor = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => api.adminSettArrangor(id, on),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-brukere'] }),
  });

  const badge = (s: KontoBruker['status']) => {
    const map: Record<KontoBruker['status'], string> = {
      active: 'bg-emerald-600/30 text-emerald-300',
      pending: 'bg-amber-600/30 text-amber-300',
      suspended: 'bg-red-600/30 text-red-300',
      rejected: 'bg-slate-700/40 text-slate-400',
    };
    return map[s];
  };

  return (
    <div>
      <div className="mb-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        >
          <option value="">Alle</option>
          <option value="active">Aktive</option>
          <option value="pending">Ventende</option>
          <option value="suspended">Suspenderte</option>
          <option value="rejected">Avslåtte</option>
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (q.data ?? []).length === 0 ? (
        <p className="text-slate-500 text-sm">Ingen brukere.</p>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {(q.data ?? []).map((u) => (
            <li key={u.id} className="p-3 bg-slate-900/30 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {u.fullName}
                  <span className={`text-xs px-2 py-0.5 rounded ${badge(u.status)}`}>{u.status}</span>
                  {u.arrangor && (
                    <span className="text-xs px-2 py-0.5 rounded bg-amber-600/30 text-amber-300">arrangør</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {u.email} • {u.foreningName ?? '—'}
                </div>
              </div>
              {u.status === 'suspended' ? (
                <button
                  onClick={() => activate.mutate(u.id)}
                  className="text-xs bg-emerald-600 hover:bg-emerald-500 px-2.5 py-1.5 rounded font-semibold shrink-0"
                >
                  Aktiver
                </button>
              ) : u.status === 'active' ? (
                <div className="flex flex-wrap justify-end gap-2 shrink-0">
                  <button
                    onClick={() => arrangor.mutate({ id: u.id, on: !u.arrangor })}
                    disabled={arrangor.isPending}
                    title="Arrangør kan styre startklokka ved komitéstyrt start, uten full admin-tilgang"
                    className="text-xs bg-slate-800 hover:bg-slate-700 px-2.5 py-1.5 rounded disabled:opacity-50"
                  >
                    {u.arrangor ? 'Fjern arrangør' : 'Gjør til arrangør'}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Suspendere ${u.fullName}? Pågående innlogginger avsluttes.`))
                        suspend.mutate(u.id);
                    }}
                    className="text-xs bg-slate-800 hover:bg-slate-700 px-2.5 py-1.5 rounded"
                  >
                    Suspendér
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
